import { Box, Card, Skeleton, Text, theme } from "@aksara-ui/react";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import MemberIcon from "assets/images/news_5364013.png";
import ReceiptIcon from "assets/images/invoice_9205255.png";
import AmountIcon from "assets/images/money_9899478.png";
import { transparentize } from "polished";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import {
  getAllRaffleCities,
  getTotalMembersRaffle,
  getTotalOcrRafflesAmountByCity,
  getTotalOcrRafflesAmountByMerchant,
  getTotalOcrRafflesByCity,
  getTotalOcrRafflesByMerchant,
  getTotalOcrRafflesByUser,
  getTotalOcrRafflesClaimed,
  getTotalRaffleWinnersByCity,
  getTotalRafflesByCity,
  getTotlOcrRafflesAmount,
} from "redux/actions";
import Loading from "components/Loading";
import ChartDataLabels from "chartjs-plugin-datalabels";
import FilterKit from "components/FilterKit";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const RaffleActivityContent: React.FC = () => {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery({ maxWidth: 576 });
  const {
    totalMembersRaffle,
    increaseMemberRaffle,
    isTotalMembersRaffleLoading,
    totalOcrRaffleClaimed,
    totalCities,
    isTotalOcrRafflesClaimedLoading,
    totalOcrRaffleAmount,
    totalIncreaseOcrRaffleAmount,
    isTotalOcrRaffleAmountLoading,
    raffleCities,
    totalOcrRaffles,
    totalOcrRafflesAmount,
    totalOcrRafflesByUser,
    raffleMerchantsForOcrClaimed,
    totalOcrRafflesByMerchant,
    raffleMerchantsForOcrAmount,
    totalOcrRafflesAmountByMerchant,
    citiesFilter,
  } = useSelector((state: any) => state.dashboardAnalyticReducer);

  const ocrRaffleOptions = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: 1,
    plugins: {
      legend: {
        position: "bottom" as const,
      },
      title: {
        display: true,
      },
      datalabels: {
        anchor: "end",
        align: "end",
        color: theme.colors.grey07,
        font: {
          weight: "bold",
        },
        formatter: (value: any, context: any) => {
          return parseInt(value).toLocaleString();
        },
      },
    },
  };

  const ocrRaffleData = {
    labels: raffleCities,
    datasets: [
      {
        label: "Raffle Receipt Claimed By City",
        data: totalOcrRaffles,
        // data dummy: [12, 19],
        backgroundColor: [
          "rgba(64, 224, 208, 0.5)",
          "rgba(255, 127, 80, 0.5)",
          "rgba(230, 230, 250, 0.5)",
          "rgba(189, 252, 201, 0.5)",
          "rgba(0, 123, 167, 0.5)",
        ],
      },
    ],
  };

  const ocrAmountOptions = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: 1,
    plugins: {
      legend: {
        position: "bottom" as const,
      },
      title: {
        display: true,
      },
      datalabels: {
        anchor: "end",
        align: "end",
        color: theme.colors.grey07,
        font: {
          weight: "bold",
        },
        formatter: (value: any, context: any) => {
          return parseInt(value).toLocaleString();
        },
      },
    },
  };

  const ocrAmountData = {
    labels: raffleCities,
    datasets: [
      {
        label: "Raffle Receipt Transaction (IDR) By City",
        data: totalOcrRafflesAmount,
        // data dummy: [12, 19],
        backgroundColor: [
          "rgba(64, 224, 208, 0.5)",
          "rgba(255, 127, 80, 0.5)",
          "rgba(230, 230, 250, 0.5)",
          "rgba(189, 252, 201, 0.5)",
          "rgba(0, 123, 167, 0.5)",
        ],
      },
    ],
  };

  const ocrRaffleByUserOptions = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: 1,
    plugins: {
      legend: {
        position: "bottom" as const,
      },
      title: {
        display: true,
      },
      datalabels: {
        anchor: "end",
        align: "end",
        color: theme.colors.grey07,
        font: {
          weight: "bold",
        },
        formatter: (value: any, context: any) => {
          return parseInt(value).toLocaleString();
        },
      },
    },
  };

  const ocrRaffleByUserData = {
    labels: raffleCities,
    datasets: [
      {
        label: "Raffle Receipt Claimed With Unique User By City",
        data: totalOcrRafflesByUser,
        // data dummy: [12, 19],
        backgroundColor: [
          "rgba(64, 224, 208, 0.5)",
          "rgba(255, 127, 80, 0.5)",
          "rgba(230, 230, 250, 0.5)",
          "rgba(189, 252, 201, 0.5)",
          "rgba(0, 123, 167, 0.5)",
        ],
      },
    ],
  };

  const ocrRaffleByMerchantOptions = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: 1,
    plugins: {
      legend: {
        position: "bottom" as const,
      },
      title: {
        display: true,
      },
      datalabels: {
        anchor: "end",
        align: "end",
        color: theme.colors.grey07,
        font: {
          weight: "bold",
        },
        formatter: (value: any, context: any) => {
          return parseInt(value).toLocaleString();
        },
      },
    },
  };

  const ocrRaffleByMerchantData = {
    labels: raffleMerchantsForOcrClaimed,
    datasets: [
      {
        label: "Raffle Receipt Claimed By Merchant",
        data: totalOcrRafflesByMerchant,
        // data dummy: [12, 19],
        backgroundColor: ["rgba(108, 92, 231, 0.7)"],
      },
    ],
  };

  const ocrAmountByMerchantOptions = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: 1,
    plugins: {
      legend: {
        position: "bottom" as const,
      },
      title: {
        display: true,
      },
      datalabels: {
        anchor: "end",
        align: "end",
        color: theme.colors.grey07,
        font: {
          weight: "bold",
        },
        formatter: (value: any, context: any) => {
          return parseInt(value).toLocaleString();
        },
      },
    },
  };

  const ocrAmountByMerchantData = {
    labels: raffleMerchantsForOcrAmount,
    datasets: [
      {
        label: "Raffle Receipt Transaction (IDR) By Merchant",
        data: totalOcrRafflesAmountByMerchant,
        // data dummy: [12, 19],
        backgroundColor: ["rgba(108, 92, 231, 0.7)"],
      },
    ],
  };

  useEffect(() => {
    dispatch(getAllRaffleCities() as any);
    dispatch(getTotalMembersRaffle() as any);
    dispatch(getTotalOcrRafflesClaimed() as any);
    dispatch(getTotlOcrRafflesAmount() as any);
    dispatch(getTotalOcrRafflesByCity() as any);
    dispatch(getTotalOcrRafflesAmountByCity() as any);
    dispatch(getTotalRafflesByCity() as any);
    dispatch(getTotalRaffleWinnersByCity() as any);
    dispatch(getTotalOcrRafflesByMerchant() as any);
    dispatch(getTotalOcrRafflesAmountByMerchant() as any);
    dispatch(getTotalOcrRafflesByUser() as any);
  }, [dispatch]);

  return (
    <Box height={"100%"} paddingY={20} paddingX={isMobile ? 30 : 50}>
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        flexDirection={isMobile ? "column" : "row"}
        marginBottom={50}
        style={{ gap: 10 }}
      >
        <Card
          elevation={2}
          width={isMobile ? "100%" : "30%"}
          padding={20}
          backgroundColor={transparentize(0.7, theme.colors.grey01)}
        >
          <Text fontWeight={700}>Total Raffle Customers</Text>
          <Box
            display={"flex"}
            justifyContent={"space-between"}
            alignItems={"center"}
            paddingTop={15}
          >
            <img
              src={MemberIcon}
              width={isMobile ? "20%" : "25%"}
              alt="people-icon"
            />
            <Box
              display={"flex"}
              flexDirection={"column"}
              textAlign={"center"}
              alignItems={"center"}
              width={"100%"}
              style={{ gap: 15 }}
            >
              {isTotalMembersRaffleLoading ? (
                <Loading />
              ) : (
                <Text
                  fontWeight={700}
                  color={theme.colors.blue06}
                  fontSize={[600, 700]}
                >
                  {totalMembersRaffle.toLocaleString()}
                </Text>
              )}

              {isTotalMembersRaffleLoading ? (
                <Box width={120}>
                  <Skeleton />
                </Box>
              ) : (
                <Text
                  color={theme.colors.grey06}
                  fontSize={100}
                  lineHeight={1.2}
                >
                  increase {increaseMemberRaffle}
                  <br />
                  member from last week
                </Text>
              )}
            </Box>
          </Box>
        </Card>

        <Card
          elevation={2}
          width={isMobile ? "100%" : "30%"}
          padding={20}
          backgroundColor={transparentize(0.7, theme.colors.red01)}
        >
          <Text fontWeight={700}>Total Receipt Claimed</Text>
          <Box
            display={"flex"}
            justifyContent={"space-between"}
            alignItems={"center"}
            paddingTop={15}
          >
            <img
              src={ReceiptIcon}
              width={isMobile ? "20%" : "25%"}
              alt="reward-icon"
            />
            <Box
              display={"flex"}
              flexDirection={"column"}
              textAlign={"center"}
              alignItems={"center"}
              width={"100%"}
              style={{ gap: 15 }}
            >
              {isTotalOcrRafflesClaimedLoading ? (
                <Loading />
              ) : (
                <Text
                  fontWeight={700}
                  color={theme.colors.blue06}
                  fontSize={[600, 700]}
                >
                  {totalOcrRaffleClaimed.toLocaleString()}
                </Text>
              )}

              {isTotalOcrRafflesClaimedLoading ? (
                <Box width={120}>
                  <Skeleton />
                </Box>
              ) : (
                <Text
                  color={theme.colors.grey06}
                  fontSize={100}
                  lineHeight={1.2}
                >
                  with {totalCities} cities participating
                </Text>
              )}
            </Box>
          </Box>
        </Card>

        <Card
          elevation={2}
          width={isMobile ? "100%" : "30%"}
          padding={20}
          backgroundColor={transparentize(0.7, theme.colors.green01)}
        >
          <Text fontWeight={700}>Total Receipt Transaction</Text>
          <Box
            display={"flex"}
            justifyContent={"space-between"}
            alignItems={"center"}
            paddingTop={15}
          >
            <img
              src={AmountIcon}
              width={isMobile ? "20%" : "25%"}
              alt="redeem-icon"
            />
            <Box
              display={"flex"}
              flexDirection={"column"}
              textAlign={"center"}
              alignItems={"center"}
              width={"100%"}
              style={{ gap: 15 }}
            >
              {isTotalOcrRaffleAmountLoading ? (
                <Loading />
              ) : (
                <>
                  <Text
                    fontWeight={700}
                    color={theme.colors.grey07}
                    fontSize={[500, 600]}
                  >
                    IDR
                  </Text>
                  <Text
                    fontWeight={700}
                    color={theme.colors.blue06}
                    fontSize={[600, 700]}
                  >
                    {parseInt(totalOcrRaffleAmount).toLocaleString()}
                  </Text>
                </>
              )}

              {isTotalOcrRaffleAmountLoading ? (
                <Box width={120}>
                  <Skeleton />
                </Box>
              ) : (
                <Text
                  color={theme.colors.grey06}
                  fontSize={100}
                  lineHeight={1.2}
                >
                  increase IDR{" "}
                  {parseInt(totalIncreaseOcrRaffleAmount).toLocaleString()} from
                  last week
                </Text>
              )}
            </Box>
          </Box>
        </Card>
      </Box>

      <Box
        display={"flex"}
        justifyContent={"space-between"}
        flexDirection={isMobile ? "column" : "row"}
        marginBottom={50}
        style={{ gap: 30 }}
      >
        <Box width={isMobile ? "100%" : "45%"} height={400}>
          <Text color="grey07" scale={400} fontWeight={"bold"}>
            RECEIPT CLAIMED BY CITY
          </Text>

          <Bar options={ocrRaffleOptions as any} data={ocrRaffleData} />
        </Box>

        <Box width={isMobile ? "100%" : "50%"} height={400}>
          <Text color="grey07" scale={400} fontWeight={"bold"}>
            RECEIPT TRANSACTION BY CITY
          </Text>

          <Bar options={ocrAmountOptions as any} data={ocrAmountData} />
        </Box>
      </Box>

      <Box
        display={"flex"}
        justifyContent={"space-between"}
        flexDirection={isMobile ? "column" : "row"}
        marginBottom={50}
        style={{ gap: 30 }}
      >
        <Box width={isMobile ? "100%" : "45%"} height={400}>
          <Text color="grey07" scale={400} fontWeight={"bold"}>
            RECEIPT CLAIMED WITH UNIQUE USER BY CITY
          </Text>

          <Bar options={ocrRaffleByUserOptions as any} data={ocrRaffleByUserData} />
        </Box>
      </Box>

      <Box
        display={"flex"}
        justifyContent={"space-between"}
        flexDirection={isMobile ? "column" : "row"}
        marginBottom={20}
        style={{ gap: 30 }}
      >
        <Box width={isMobile ? "100%" : "45%"} height={500}>
          <Text color="grey07" scale={400} fontWeight={"bold"}>
            RECEIPT CLAIMED BY MERCHANT
          </Text>
          <Box marginTop={10} />
          <FilterKit
            orderByOptions={[
              {
                key: "city1",
                label: "Samarinda",
                options: citiesFilter.map((city: any) => {
                  return {
                    label: city.city,
                    value: city.id ?? "",
                  };
                }),
              },
            ]}
            onChangeOrder={(e: any) => {
              const selectedCity = e.city1 ? e.city1.value : "";
              dispatch(getTotalOcrRafflesByMerchant(selectedCity) as any);
            }}
          />

          <Bar
            options={ocrRaffleByMerchantOptions as any}
            data={ocrRaffleByMerchantData}
          />
        </Box>

        <Box width={isMobile ? "100%" : "50%"} height={500}>
          <Text color="grey07" scale={400} fontWeight={"bold"}>
            RECEIPT TRANSACTION BY MERCHANT
          </Text>
          <Box marginTop={10} />
          <FilterKit
            orderByOptions={[
              {
                key: "city2",
                label: "Samarinda",
                options: citiesFilter.map((city: any) => {
                  return {
                    label: city.city,
                    value: city.id ?? "",
                  };
                }),
              },
            ]}
            onChangeOrder={(e: any) => {
              const selectedCity = e.city2 ? e.city2.value : "";
              dispatch(getTotalOcrRafflesAmountByMerchant(selectedCity) as any);
            }}
          />

          <Bar
            options={ocrAmountByMerchantOptions as any}
            data={ocrAmountByMerchantData}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default RaffleActivityContent;
